import { useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, useMap } from 'react-leaflet'
import { Box, Button, Heading, Select, Stack, Text } from '@ui'
import { formatDate } from '../format.js'
import { PhotoMonths, PhotoViewer } from './Photos.jsx'
import { BaseLayers, OSM_ATTRIBUTION, PhotoPins } from './WalkMap.jsx'

// Photos this far from each other (metres, chained) count as one area; the map opens on the
// biggest, since most photos are near home and the whole set spans the country.
const AREA_METERS = 20000

function metresApart(a, b) {
  const r = Math.PI / 180
  const x = Math.sin(((b.lat - a.lat) * r) / 2) ** 2
    + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(((b.lon - a.lon) * r) / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(x))
}

// The pins of the area holding the most photos (single-link, AREA_METERS).
function biggestArea(pins) {
  const areas = []
  for (const pin of pins) {
    const near = areas.filter((a) => a.some((p) => metresApart(p, pin) < AREA_METERS))
    const merged = near.flat().concat(pin)
    near.forEach((a) => areas.splice(areas.indexOf(a), 1))
    areas.push(merged)
  }
  return areas.reduce((best, a) => (a.length > best.length ? a : best), [])
}

const boundsOf = (pins) => pins.map((p) => [p.lat, p.lon])

// Fits the map to pins whenever fit changes (a new photo set, or "Show all").
function Fit({ fit }) {
  const map = useMap()
  useEffect(() => {
    if (!fit?.length) return
    if (fit.length === 1) map.setView(fit[0], 15)
    else map.fitBounds(fit, { padding: [32, 32], maxZoom: 16 })
  }, [map, fit])
  return null
}

const plural = (n, word) => `${n.toLocaleString('en-GB')} ${word}${n === 1 ? '' : 's'}`

// "3 photos and a video, Sun 13 Sep 2026" or "…, 2 May 2023 – 13 Sep 2026"
function groupLabel(group) {
  const videos = group.pins.filter((p) => p.photo.media_type === 'video').length
  const photos = group.pins.length - videos
  const what = [photos && plural(photos, 'photo'), videos && plural(videos, 'video')].filter(Boolean).join(' and ')
  const days = group.pins.map((p) => p.photo.taken_at ?? p.photo.start_time).filter(Boolean).sort()
  if (!days.length) return what
  const first = formatDate(days[0])
  const last = formatDate(days[days.length - 1])
  return `${what}, ${first === last ? first : `${first} – ${last}`}`
}

// "Go to a place" (2026-10-25): the counties (and, where a county's photos span several, their
// districts) of photos' places (places: id → {county, district}, from /api/photos/places, named on
// the server from ONS districts), most photos first, with counts. Values: '' (all places),
// 'c|<county>' or 'd|<county>|<district>'; county '' is outside the UK.
export function placeGroups(photos, places) {
  const counties = new Map()
  for (const p of photos) {
    const place = places.get(p.id)
    if (!place) continue
    const county = place.county ?? ''
    if (!counties.has(county)) counties.set(county, { name: county, ids: new Set(), districts: new Map() })
    const c = counties.get(county)
    c.ids.add(p.id)
    if (place.district) {
      if (!c.districts.has(place.district)) c.districts.set(place.district, new Set())
      c.districts.get(place.district).add(p.id)
    }
  }
  const bySize = (a, b) => b.ids.size - a.ids.size || a.name.localeCompare(b.name)
  return [...counties.values()].sort(bySize).map((c) => ({
    ...c,
    label: c.name || 'Outside the UK',
    districts: [...c.districts].map(([name, ids]) => ({ name, ids })).sort(bySize)
      .filter((d, _, all) => all.length > 1 || d.name !== c.name),
  }))
}

// Which photo ids and what name a "Go to a place" value stands for (null for all places).
export function placeOf(groups, value) {
  const [kind, county, district] = value.split('|')
  const c = groups.find((g) => g.name === county)
  if (!c) return null
  if (kind === 'c') return { ids: c.ids, label: c.label }
  const d = c.districts.find((x) => x.name === district)
  return d ? { ids: d.ids, label: `${d.name}, ${c.label}` } : null
}

// The "Go to a place" dropdown for placeGroups' groups.
export function PlacePicker({ groups, value, onChange }) {
  const n = (k) => k.toLocaleString('en-GB')
  return (
    <Select size="sm" autoWidth aria-label="Go to a place" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Go to a place…</option>
      {groups.map((c) => (c.districts.length > 0 ? (
        <optgroup key={c.name} label={c.label}>
          <option value={`c|${c.name}`}>All of {c.label} ({n(c.ids.size)})</option>
          {c.districts.map((d) => <option key={d.name} value={`d|${c.name}|${d.name}`}>{d.name} ({n(d.ids.size)})</option>)}
        </optgroup>
      ) : (
        <option key={c.name} value={`c|${c.name}`}>{c.label} ({n(c.ids.size)})</option>
      )))}
    </Select>
  )
}

// The photos page's map: every photo with a place (places: id → {lat, lon, from}, from
// /api/photos/places) as the walk map's thumbnail pins, grouped where they'd overlap at the
// current zoom. Clicking a group lists its photos below the map as the date view does (by month,
// newest first), and a thumbnail there opens the viewer over that group. resetKey (the year
// filter) says when the set is a new one: the map then refits and the list closes (a deletion or
// a new time changes photos but keeps both). focus ({key, ids, label}, from "Go to a place")
// fits the map to those photos and lists them below; a new key moves again, ids null shows all.
// A shared Photos page (SharedPhotos.jsx) passes
// tiles 'osm' (OpenStreetMap's own tiles: the OS proxy is signed-in only), readOnly and its own
// caption/walkLabel for walks it can't link to.
export default function PhotoMap({
  photos, places, resetKey, focus = null, onDeleted, onUpdated, tiles = 'os', readOnly = false, caption, walkLabel,
}) {
  const pins = useMemo(
    () => photos.flatMap((photo, index) => {
      const place = places.get(photo.id)
      return place ? [{ index, photo, lat: place.lat, lon: place.lon, from: place.from }] : []
    }),
    [photos, places],
  )
  const [fit, setFit] = useState(null)
  // The chosen group as photo ids, so a deletion or a new time shows at once.
  const [chosen, setChosen] = useState(null)
  const [chosenLabel, setChosenLabel] = useState(null) // the place's name, when chosen from focus
  const [index, setIndex] = useState(null)
  useEffect(() => {
    setFit(boundsOf(biggestArea(pins)))
    setChosen(null)
    setIndex(null)
  }, [resetKey])
  useEffect(() => {
    if (!focus) return
    setIndex(null)
    if (!focus.ids) {
      setFit(boundsOf(pins))
      setChosen(null)
      return
    }
    setFit(boundsOf(pins.filter((p) => focus.ids.has(p.photo.id))))
    setChosen(new Set(focus.ids))
    setChosenLabel(focus.label)
  }, [focus?.key])
  const selected = useMemo(() => (chosen ? photos.filter((p) => chosen.has(p.id)) : []), [photos, chosen])
  const panel = useRef(null)
  const choose = (group) => {
    setChosen(new Set(group.pins.map((p) => p.photo.id)))
    setChosenLabel(null)
    setIndex(null)
    // After it renders: bring the list's heading just into view, so the map stays on screen.
    requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }))
  }

  const unplaced = photos.length - pins.length
  if (pins.length === 0) {
    return <Text as="p" muted>None of these photos has a place on the map.</Text>
  }
  return (
    <>
      <Box position="relative">
        <MapContainer className="walk-map photo-map" center={[pins[0].lat, pins[0].lon]} zoom={12} minZoom={7} maxZoom={tiles === 'osm' ? 19 : 20} scrollWheelZoom={false}>
          {tiles === 'osm'
            ? <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution={OSM_ATTRIBUTION} maxZoom={19} />
            : <BaseLayers />}
          <PhotoPins pins={pins} onGroup={choose} label={groupLabel} />
          <Fit fit={fit} />
        </MapContainer>
      </Box>
      <Stack wrap align="center" gap={2} mt={1}>
        <Text as="p" text="small" muted mb={0} me="auto">
          Click a picture on the map to see the photos there.
          {' '}Photos without a location of their own are placed on the route at the time they were taken
          {unplaced > 0 && <>; {unplaced.toLocaleString('en-GB')} {unplaced === 1 ? 'has' : 'have'} no place (no time, or taken outside the walk)</>}.
        </Text>
        <Button outline variant="secondary" size="sm" onClick={() => setFit(boundsOf(pins))}>Show all</Button>
      </Stack>

      {chosen && (
        <Box as="section" mt={3}>
          <Stack align="center" gap={2} mb={2} ref={panel}>
            <Heading level={2} size={4} mb={0} me="auto">
              {selected.length === 0 ? 'No photos left here' : `${chosenLabel ? `${chosenLabel}: ` : ''}${groupLabel({ pins: selected.map((photo) => ({ photo })) })}`}
            </Heading>
            <Button outline variant="secondary" size="sm" onClick={() => setChosen(null)}>Close</Button>
          </Stack>
          <PhotoMonths photos={selected} onOpen={setIndex} headingLevel={3} caption={caption} linkWalks={!readOnly} />
        </Box>
      )}

      <PhotoViewer
        photos={selected} index={index} onIndex={setIndex} showWalk walkLabel={walkLabel} readOnly={readOnly}
        onDeleted={readOnly ? undefined : (gone) => {
          onDeleted(gone)
          setIndex(selected.length > 1 ? Math.min(index, selected.length - 2) : null)
        }}
        onUpdated={onUpdated}
      />
    </>
  )
}
